import React, { useEffect, useRef, useState } from 'react';
import { TableSchema, TableColumn } from '../pages/App';
import RecordModal from './RecordModal';
import UploadModal from './UploadModal';
import PlanFillModal from './PlanFillModal';
import BulkEditModal from './BulkEditModal';
import { apiFetch } from '../api';
import { getPrimaryKey } from '../utils/primaryKey';

type RecordData = Record<string, any>;

interface Props {
  schema: TableSchema;
}

type SortState = { key: string; direction: 'asc' | 'desc' } | null;

const DataTable: React.FC<Props> = ({ schema }) => {
  const [data, setData] = useState<RecordData[]>([]);
  const [sort, setSort] = useState<SortState>(null);
  const [filterInput, setFilterInput] = useState<Record<string, string>>({});
  const [filterSelect, setFilterSelect] = useState<Record<string, Set<any>>>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [editing, setEditing] = useState<RecordData | null>(null);
  const [adding, setAdding] = useState(false);
  const [bulkEditing, setBulkEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [showId, setShowId] = useState(false);
  const [collapsedRows, setCollapsedRows] = useState<Set<number>>(new Set());
  const [focusRowId, setFocusRowId] = useState<number | null>(null);
  const [showPlanFill, setShowPlanFill] = useState(false);
  const isArticles = schema.name === 'articles';
  const isFinanceOps = schema.name === 'finance_operations';
  const isMappings = schema.name === 'mappings';
  const isQueries = schema.name === 'queries';
  const isPromoTask = schema.name === 'promo_task';
  const requiresDateRange =
    schema.name === 'fact_records' || isFinanceOps || isQueries;
  const apiTableName = schema.name.replace(/-/g, '_');
  const [mode, setMode] = useState<'table' | 'tree'>('table');
  const containerRef = useRef<HTMLDivElement>(null);
  const articleMenuRef = useRef<HTMLDivElement>(null);
  const articleFileRef = useRef<HTMLInputElement>(null);
  const financeMenuRef = useRef<HTMLDivElement>(null);
  const financeFileRef = useRef<HTMLInputElement>(null);
  const mappingMenuRef = useRef<HTMLDivElement>(null);
  const mappingFileRef = useRef<HTMLInputElement>(null);
  const [references, setReferences] = useState<Record<string, any[]>>({});
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>({});
  const [isFilterOptionsLoading, setIsFilterOptionsLoading] = useState(false);
  const [isArticleMenuOpen, setIsArticleMenuOpen] = useState(false);
  const [isFinanceMenuOpen, setIsFinanceMenuOpen] = useState(false);
  const [isMappingMenuOpen, setIsMappingMenuOpen] = useState(false);
  const [isDeleteMenuOpen, setIsDeleteMenuOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    if (schema.name === 'plan_records') {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    }
    return '';
  });

  const getDefaultDateRange = (tableName: string) => {
    const now = new Date();
    if (tableName === 'fact_records' || tableName === 'finance_operations') {
      return {
        from: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`,
        to: new Date().toISOString().slice(0, 10),
      };
    }

    if (tableName === 'queries') {
      const start = new Date(now);
      start.setDate(now.getDate() - 7);
      const toIso = (d: Date) => d.toISOString().slice(0, 10);
      return { from: toIso(start), to: toIso(now) };
    }

    return { from: '', to: '' };
  };

  const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange(schema.name).from);
  const [dateTo, setDateTo] = useState(() => getDefaultDateRange(schema.name).to);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const pageSize = 100;

  function formatNumber(value: any) {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    const hasFraction = Math.abs(num % 1) > 0;
    const fractionDigits = hasFraction ? 2 : 0;
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(num);
  }

  function normalizeDateString(value: string): string {
    let v = value.trim();
    v = v.replace(' ', 'T');
    v = v.replace(/(\.\d{3})\d+/, '$1');
    return v;
  }

  function parseUtcDate(value: any): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === 'string') {
      const normalized = normalizeDateString(value);
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized);
      if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        return new Date(`${normalized}T00:00:00Z`);
      }
      if (/^\d{4}-\d{2}-\d{2}T/.test(normalized) && !hasTz) {
        return new Date(`${normalized}Z`);
      }
      return new Date(normalized);
    }
    return new Date(value);
  }

  function formatDateValue(value: any) {
    if (!value) return '';
    const showTimeFromString = typeof value === 'string' && /[T ]\d{2}:\d{2}/.test(value);
    const date = parseUtcDate(value);
    if (!date || Number.isNaN(date.getTime())) return value;
    const hasTime =
      showTimeFromString ||
      date.getUTCHours() !== 0 ||
      date.getUTCMinutes() !== 0 ||
      date.getUTCSeconds() !== 0 ||
      date.getUTCMilliseconds() !== 0;
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = String(date.getUTCFullYear()).slice(-2);
    if (!hasTime) {
      return `${day}.${month}.${year}`;
    }
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  function stringifyObject(value: any) {
    if (value == null) return '';
    if (typeof value !== 'object') return String(value);
    if ('raw' in value) {
      const rawValue = (value as { raw?: unknown }).raw;
      if (rawValue == null) return '';
      if (typeof rawValue === 'object') {
        try {
          return JSON.stringify(rawValue);
        } catch (e) {
          return String(rawValue);
        }
      }
      return String(rawValue);
    }
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }

  function formatDateShort(value: any) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  }

  function toCamel(str: string) {
    return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  }

  function toSnake(str: string) {
    return str
      .replace(/([A-Z])/g, letter => `_${letter.toLowerCase()}`)
      .replace(/^_/, '');
  }

  function getRefLabel(item: any, table?: string): string {
    if (!item) return '';
    if (table === 'finance_operations') return String(item.id);
    if (typeof item.alias === 'string' && item.alias.trim()) return item.alias;
    if ('name' in item) return item.name;
    const key = Object.keys(item).find(k => k !== 'id');
    return key ? String(item[key]) : String(item.id);
  }

  function getOptionLabel(column: TableColumn, option: any) {
    if (column.type === 'reference' && references[column.key]) {
      return getRefLabel(
        references[column.key].find(r => r.id === option),
        column.refTable
      );
    }
    if (column.type === 'date') {
      return String(formatDateValue(option));
    }
    if (column.type === 'number') {
      return String(formatNumber(option));
    }
    return String(option);
  }

  const primaryKey = React.useMemo(() => getPrimaryKey(schema), [schema]);
  const idIndex = schema.columns.findIndex(c => c.key === 'id');
  const visibleColumns = schema.columns.filter(
    c => (c.key !== 'id' || showId) && !(isPromoTask && c.key === 'data')
  );
  const firstVisibleKey = visibleColumns.find(c => c.key !== 'id')?.key;
  const getRowKey = (row: RecordData) => row[primaryKey];

  const columnOptions = React.useMemo(() => {
    if (schema.name === 'fact_records' || schema.name === 'finance_operations') {
      return filterOptions;
    }
    const opts: Record<string, any[]> = {};
    data.forEach(row => {
      visibleColumns.forEach(col => {
        const val = row[col.key];
        if (val === undefined || val === null) return;
        if (!opts[col.key]) opts[col.key] = [];
        if (!opts[col.key].includes(val)) opts[col.key].push(val);
      });
    });
    return opts;
  }, [data, visibleColumns, schema.name, filterOptions]);

  const useServerFiltering = true;

  const filteredData = React.useMemo(() => {
    if (useServerFiltering) {
      return data;
    }
    return data.filter(row =>
      visibleColumns.every(col => {
        const val = row[col.key];
        const label =
          col.type === 'reference' && references[col.key]
            ? getRefLabel(
                references[col.key].find(r => r.id === val),
                col.refTable
              )
            : val;
        const inputVal = filterInput[col.key];
        const selected = filterSelect[col.key];
        const matchInput = inputVal
          ? String(label ?? '')
              .toLowerCase()
              .includes(String(inputVal).toLowerCase())
          : true;
        const matchSelect = selected && selected.size > 0 ? selected.has(val) : true;
        return matchInput && matchSelect;
      })
    );
  }, [data, filterInput, filterSelect, visibleColumns, references, useServerFiltering]);

  const hasFilters = React.useMemo(
    () =>
      Object.values(filterInput).some(v => v && v.trim() !== '') ||
      Object.values(filterSelect).some(s => s && s.size > 0),
    [filterInput, filterSelect]
  );

  const bulkFilters = React.useMemo(() => {
    const filters: Record<string, any[]> = {};
    Object.entries(filterSelect).forEach(([key, selected]) => {
      if (selected && selected.size > 0) {
        filters[key] = Array.from(selected);
      }
    });
    const search: Record<string, string> = {};
    Object.entries(filterInput).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (trimmed) {
        search[key] = trimmed;
      }
    });
    return { filters, search };
  }, [filterSelect, filterInput]);

  const isColumnFiltered = (key: string) => {
    const hasSelection = filterSelect[key]?.size > 0;
    const hasInput = (filterInput[key]?.trim() ?? '') !== '';
    return hasSelection || (!useServerFiltering && hasInput);
  };

  const totals = React.useMemo(() => {
    if (
      schema.name !== 'finance_operations' &&
      schema.name !== 'fact_records' &&
      schema.name !== 'plan_records'
    ) {
      return {} as Record<string, number>;
    }
    const sums: Record<string, number> = { count: filteredData.length };
    schema.columns.forEach(col => {
      if (col.type === 'number') {
        sums[col.key] = filteredData.reduce((acc, row) => {
          const val = row[col.key];
          const num = val === undefined || val === null ? 0 : Number(val);
          return acc + (Number.isNaN(num) ? 0 : num);
        }, 0);
      }
    });
    return sums;
  }, [schema.name, schema.columns, filteredData]);

  const useServerSorting =
    schema.name === 'finance_operations' || schema.name === 'fact_records';

  const sortedData = React.useMemo(() => {
    if (useServerSorting || !sort) return filteredData;
    const arr = [...filteredData];
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (av === bv) return 0;
      if (av === undefined || av === null) return sort.direction === 'asc' ? -1 : 1;
      if (bv === undefined || bv === null) return sort.direction === 'asc' ? 1 : -1;
      if (av < bv) return sort.direction === 'asc' ? -1 : 1;
      if (av > bv) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredData, sort, useServerSorting]);

  const parentField = React.useMemo(() => {
    if (isArticles) {
      return schema.columns.some(c => c.key === 'parentId') ? 'parentId' : 'parent_id';
    }
    if (isFinanceOps) {
      return schema.columns.some(c => c.key === 'relationId') ? 'relationId' : 'relation_id';
    }
    return null;
  }, [schema, isArticles, isFinanceOps]);

  const rowsToRender = React.useMemo(() => {
    const treeMode = isArticles || (isFinanceOps && mode === 'tree');
    if (!treeMode || !parentField) {
      return sortedData.map(r => ({ row: r, level: 0, hasChildren: false }));
    }
    const map = new Map<string | number | null, RecordData[]>();
    sortedData.forEach(r => {
      const pid = (r as any)[parentField] ?? null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid)!.push(r);
    });
    const build = (pid: string | number | null, level: number): { row: RecordData; level: number; hasChildren: boolean }[] => {
      const items = map.get(pid) || [];
      const result: { row: RecordData; level: number; hasChildren: boolean }[] = [];
      for (const it of items) {
        const itemId = getRowKey(it);
        const children = map.get(itemId) || [];
        const hasChildren = children.length > 0;
        result.push({ row: it, level, hasChildren });
        if (!collapsedRows.has(itemId)) {
          result.push(...build(itemId, level + 1));
        }
      }
      return result;
    };
    return build(null, 0);
  }, [sortedData, collapsedRows, isArticles, isFinanceOps, mode, parentField, primaryKey]);

  const displayedRowIds = React.useMemo(() => {
    const ids = new Set<string | number>();
    rowsToRender.forEach(({ row }) => {
      const rowId = getRowKey(row);
      if (rowId !== undefined && rowId !== null) {
        ids.add(rowId);
      }
    });
    return Array.from(ids);
  }, [rowsToRender, getRowKey]);

  useEffect(() => {
    if (uploadMsg) {
      const t = setTimeout(() => setUploadMsg(null), 5000);
      return () => clearTimeout(t);
    }
  }, [uploadMsg]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!openFilter) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest(`[data-filter-root="${openFilter}"]`)) return;
      setOpenFilter(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openFilter]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isArticleMenuOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-article-menu-root]')) return;
      setIsArticleMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isArticleMenuOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isMappingMenuOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-mapping-menu-root]')) return;
      setIsMappingMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isMappingMenuOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isFinanceMenuOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-finance-menu-root]')) return;
      setIsFinanceMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isFinanceMenuOpen]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!isDeleteMenuOpen) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-delete-menu-root]')) return;
      setIsDeleteMenuOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [isDeleteMenuOpen]);

  useEffect(() => {
    if (
      !requiresDateRange ||
      (dateFrom && dateTo)
    ) {
      loadData(true);
    }
    schema.columns.forEach(async col => {
      if (col.type === 'reference' && col.refTable) {
        const res = await apiFetch(`/api/${col.refTable}`);
        if (!res.ok) {
          setReferences(current => ({ ...current, [col.key]: [] }));
          return;
        }
        const data = await res.json();
        setReferences(current => ({
          ...current,
          [col.key]: Array.isArray(data) ? data : [],
        }));
      }
    });
  }, [schema]);

  useEffect(() => {
    const defaults = getDefaultDateRange(schema.name);
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setOffset(0);
    setHasMore(true);
    setTotalCount(null);
  }, [schema.name]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (!requiresDateRange || (dateFrom && dateTo)) {
        loadData(true);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [filterInput, filterSelect, sort, dateFrom, dateTo, month]);

  useEffect(() => {
    if (requiresDateRange && dateFrom && dateTo) {
      loadData(true);
      if (schema.name === 'fact_records' || schema.name === 'finance_operations') {
        loadFilterOptions();
      }
    }
  }, [dateFrom, dateTo, requiresDateRange, schema.name]);

  useEffect(() => {
    if (schema.name === 'plan_records' && month) {
      loadData(true);
    }
  }, [month]);

  useEffect(() => {
    if (schema.name !== 'finance_operations') {
      return;
    }
    if (dateFrom && dateTo) {
      loadData(true);
    }
  }, [filterSelect, filterInput, sort, schema.name, references, dateFrom, dateTo]);

  useEffect(() => {
    if (schema.name !== 'fact_records') {
      return;
    }
    if (dateFrom && dateTo) {
      loadData(true);
    }
  }, [sort, schema.name, dateFrom, dateTo]);

  useEffect(() => {
    if (schema.name !== 'fact_records' && schema.name !== 'finance_operations') {
      setFilterOptions({});
    }
  }, [schema.name]);

  useEffect(() => {
    if (!focusRowId || !containerRef.current) return;
    const row = containerRef.current.querySelector<HTMLTableRowElement>(
      `tr[data-row-id="${focusRowId}"]`
    );
    if (!row) {
      setFocusRowId(null);
      return;
    }
    row.scrollIntoView({ block: 'center' });
    row.focus({ preventScroll: true });
    setFocusRowId(null);
  }, [rowsToRender, focusRowId]);

  const buildListParams = (offsetValue: number, limitValue: number) => {
    const params = new URLSearchParams();
    if (requiresDateRange && dateFrom && dateTo) {
      params.set('dateFrom', dateFrom);
      params.set('dateTo', dateTo);
    }
    if (schema.name === 'plan_records' && month) {
      params.set('month', month);
    }
    params.set('offset', String(offsetValue));
    params.set('limit', String(limitValue));
    if (sort) {
      params.set('sortKey', sort.key);
      params.set('sortDir', sort.direction);
    }
    const keys = new Set([...Object.keys(filterSelect)]);
    keys.forEach(key => {
      const selectedValues = filterSelect[key];
      if (selectedValues && selectedValues.size > 0) {
        selectedValues.forEach(val => {
          params.append(`filter_${key}`, String(val));
        });
      }
    });
    Object.entries(filterInput).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (trimmed) {
        params.set(`search_${key}`, trimmed);
      }
    });
    return params.toString();
  };

  const buildExportParams = () => {
    const params = new URLSearchParams();
    if (requiresDateRange && dateFrom && dateTo) {
      params.set('dateFrom', dateFrom);
      params.set('dateTo', dateTo);
    }
    if (schema.name === 'plan_records' && month) {
      params.set('month', month);
    }
    if (sort) {
      params.set('sortKey', sort.key);
      params.set('sortDir', sort.direction);
    }
    const keys = new Set([...Object.keys(filterSelect)]);
    keys.forEach(key => {
      const selectedValues = filterSelect[key];
      if (selectedValues && selectedValues.size > 0) {
        selectedValues.forEach(val => {
          params.append(`filter_${key}`, String(val));
        });
      }
    });
    Object.entries(filterInput).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (trimmed) {
        params.set(`search_${key}`, trimmed);
      }
    });
    return params.toString();
  };

  const getApiTablePath = () => schema.name;

  const parseTotalCount = (res: Response, fallback: number | null) => {
    const header = res.headers.get('X-Total-Count');
    if (!header) return fallback;
    const parsed = Number(header);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const fetchPage = async (offsetValue: number, limitValue: number) => {
    const params = buildListParams(offsetValue, limitValue);
    const apiPath = getApiTablePath();
    const url = params ? `/api/table/${apiPath}?${params}` : `/api/table/${apiPath}`;
    const res = await apiFetch(url);
    if (!res.ok) {
      throw new Error('Таблица не найдена');
    }
    const json = await res.json();
    const rows = Array.isArray(json) ? json : [];
    const total = parseTotalCount(res, null);
    return { rows, total };
  };

  async function loadData(reset = false) {
    if (reset) {
      setIsLoading(true);
      setLoadingMore(false);
    } else {
      setLoadingMore(true);
    }
    setLoadError(null);
    try {
      if (requiresDateRange && (!dateFrom || !dateTo)) {
        setData([]);
        setHasMore(false);
        return;
      }
      const currentOffset = reset ? 0 : offset;
      const { rows, total } = await fetchPage(currentOffset, pageSize);
      setData(current => (reset ? rows : [...current, ...rows]));
      setOffset(current => (reset ? rows.length : current + rows.length));
      setHasMore(rows.length === pageSize);
      if (total !== null) {
        setTotalCount(total);
      } else if (reset) {
        setTotalCount(rows.length);
      }
    } finally {
      if (reset) {
        setIsLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }

  async function loadFilterOptions() {
    setIsFilterOptionsLoading(true);
    try {
      if (schema.name === 'fact_records') {
        if (!dateFrom || !dateTo) {
          setFilterOptions({});
          return;
        }
        const res = await apiFetch(
          `/api/fact-records/filters?dateFrom=${dateFrom}&dateTo=${dateTo}`
        );
        setFilterOptions(await res.json());
      } else if (schema.name === 'finance_operations') {
        const params = dateFrom && dateTo ? `?dateFrom=${dateFrom}&dateTo=${dateTo}` : '';
        const res = await apiFetch(`/api/finance-operations/filters${params}`);
        setFilterOptions(await res.json());
      }
    } finally {
      setIsFilterOptionsLoading(false);
    }
  }

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      if (
        hasMore &&
        !loadingMore &&
        el.scrollTop + el.clientHeight >= el.scrollHeight - 50
      ) {
        loadData(false);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [schema.name, hasMore, loadingMore, dateFrom, dateTo]);

  function handleSort(column: TableColumn) {
    if (sort?.key === column.key) {
      setSort({ key: column.key, direction: sort.direction === 'asc' ? 'desc' : 'asc' });
    } else {
      setSort({ key: column.key, direction: 'asc' });
    }
  }

  async function handleDelete(id: string | number) {
    try {
      const res = await apiFetch(`/api/${schema.name}/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(formatDeleteError(payload) || res.statusText);
      }
      loadData();
    } catch (e: any) {
      setUploadMsg('ошибка удаления: ' + (e.message || ''));
    }
  }

  async function handleLoadAll() {
    if (totalCount === null) return;
    if (data.length >= totalCount) return;
    const ok = confirm(`Загрузить все записи по фильтру (${totalCount})?`);
    if (!ok) return;
    setIsLoadingAll(true);
    setIsLoading(true);
    setLoadError(null);
    try {
      const chunkSize = 1000;
      const allRows: RecordData[] = [];
      let currentOffset = 0;
      while (currentOffset < totalCount) {
        const { rows } = await fetchPage(currentOffset, chunkSize);
        allRows.push(...rows);
        if (rows.length < chunkSize) break;
        currentOffset += rows.length;
      }
      setData(allRows);
      setOffset(allRows.length);
      setHasMore(false);
    } catch (e: any) {
      setLoadError(e.message || 'Не удалось загрузить данные');
    } finally {
      setIsLoadingAll(false);
      setIsLoading(false);
    }
  }

  async function handleUpload(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await apiFetch('/api/fact-records/upload', { method: 'POST', body: formData });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      await res.json();
      setUploadMsg('файл успешно выгружен');
      loadData();
    } catch (e: any) {
      setUploadMsg('выгрузка завершилась с ошибкой : ' + (e.message || '')); 
    } finally {
      setUploading(false);
      setShowUpload(false);
    }
  }

  async function handleCreateOps() {
    if (!dateFrom || !dateTo) return;
    setUploading(true);
    try {
      const res = await apiFetch('/api/fact-records/create-operations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFrom, dateTo })
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      await res.json();
      setUploadMsg('финансовые операции созданы');
    } catch (e: any) {
      setUploadMsg('ошибка создания ФО : ' + (e.message || ''));
    } finally {
      setUploading(false);
    }
  }

  const buildFinanceExportParams = () => {
    const params = new URLSearchParams();
    if (dateFrom && dateTo) {
      params.set('dateFrom', dateFrom);
      params.set('dateTo', dateTo);
    }
    if (sort) {
      params.set('sortKey', sort.key);
      params.set('sortDir', sort.direction);
    }
    const keys = new Set([...Object.keys(filterSelect)]);
    keys.forEach(key => {
      const selectedValues = filterSelect[key];
      if (selectedValues && selectedValues.size > 0) {
        selectedValues.forEach(val => {
          params.append(`filter_${key}`, String(val));
        });
      }
    });
    Object.entries(filterInput).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (trimmed) {
        params.set(`search_${key}`, trimmed);
      }
    });
    return params.toString();
  };

  const buildDeleteFiltersPayload = () => {
    const filters: Record<string, string[]> = {};
    const keys = new Set([...Object.keys(filterSelect)]);
    keys.forEach(key => {
      const selectedValues = filterSelect[key];
      if (selectedValues && selectedValues.size > 0) {
        filters[key] = Array.from(selectedValues).map(val => String(val));
      }
    });
    const search: Record<string, string> = {};
    Object.entries(filterInput).forEach(([key, value]) => {
      const trimmed = value?.trim();
      if (trimmed) {
        search[key] = trimmed;
      }
    });
    return {
      filters,
      search,
      dateFrom,
      dateTo,
      month,
    };
  };

  const formatDeleteError = (payload: any) => {
    if (!payload) return 'ошибка удаления';
    const message = payload.message || payload.detail || 'ошибка удаления';
    const blockers = payload.blockers;
    if (!Array.isArray(blockers) || blockers.length === 0) {
      return message;
    }
    const lines = blockers.map((b: any) => {
      const table = b.table || 'unknown';
      const field = b.field || 'unknown';
      const recordId = b.record_id ?? b.recordId ?? '?';
      const refValue = b.ref_value ?? b.refValue ?? '?';
      return `${table}.${field} -> запись ${recordId} (ссылка ${refValue})`;
    });
    return `${message}. Связи: ${lines.join('; ')}`;
  };

  function downloadBlobFile(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleExportExcel() {
    if (requiresDateRange && (!dateFrom || !dateTo)) return;
    setUploading(true);
    try {
      const params = buildExportParams();
      const apiPath = getApiTablePath();
      const url = params ? `/api/table/${apiPath}/export?${params}` : `/api/table/${apiPath}/export`;
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: schema.label || schema.name,
          columns: visibleColumns.map(col => ({ key: col.key, label: col.label })),
        }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const blob = await res.blob();
      const periodSuffix =
        dateFrom && dateTo ? `-${dateFrom}-${dateTo}` : month ? `-${month}` : '';
      const filename = `${apiPath}${periodSuffix}.xlsx`;
      downloadBlobFile(blob, filename);
      setUploadMsg('выгрузка завершена');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
    }
  }

  async function handleExportFinance() {
    setUploading(true);
    try {
      const params = buildFinanceExportParams();
      const res = await apiFetch(`/api/finance-operations/export?${params}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const exportedCount = res.headers.get('X-Exported-Count');
      const blob = await res.blob();
      const filename = `finance-operations${dateFrom && dateTo ? `-${dateFrom}-${dateTo}` : ''}.xlsx`;
      downloadBlobFile(blob, filename);
      setUploadMsg(
        exportedCount ? `выгружено операций: ${exportedCount}` : 'выгрузка завершена'
      );
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsFinanceMenuOpen(false);
    }
  }

  async function handleTemplateFinance() {
    setUploading(true);
    try {
      const res = await apiFetch('/api/finance-operations/template');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const blob = await res.blob();
      downloadBlobFile(blob, 'finance-operations-template.xlsx');
      setUploadMsg('шаблон выгружен');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки шаблона: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsFinanceMenuOpen(false);
    }
  }

  async function handleImportFinance(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await apiFetch('/api/finance-operations/import', {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`загружено: ${json.inserted ?? 0}, обновлено: ${json.updated ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка загрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsFinanceMenuOpen(false);
      if (financeFileRef.current) {
        financeFileRef.current.value = '';
      }
    }
  }

  function downloadJsonFile(data: unknown, filename: string) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  async function handleExportArticles() {
    setUploading(true);
    try {
      const res = await apiFetch('/api/articles/export');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      downloadJsonFile(json, 'articles.json');
      setUploadMsg('статьи выгружены');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsArticleMenuOpen(false);
    }
  }

  async function handleExportMappings() {
    setUploading(true);
    try {
      const res = await apiFetch('/api/mappings/export');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      downloadJsonFile(json, 'mappings.json');
      setUploadMsg('сопоставление выгружено');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsMappingMenuOpen(false);
    }
  }

  async function handleTemplateMappings() {
    setUploading(true);
    try {
      const res = await apiFetch('/api/mappings/template');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      downloadJsonFile(json, 'mappings-template.json');
      setUploadMsg('шаблон выгружен');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки шаблона: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsMappingMenuOpen(false);
    }
  }

  async function handleImportMappings(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await apiFetch('/api/mappings/import', { method: 'POST', body: formData });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`загружено: ${json.inserted ?? 0}, обновлено: ${json.updated ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка загрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsMappingMenuOpen(false);
      if (mappingFileRef.current) {
        mappingFileRef.current.value = '';
      }
    }
  }

  async function handleTemplateArticles() {
    setUploading(true);
    try {
      const res = await apiFetch('/api/articles/template');
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      downloadJsonFile(json, 'articles-template.json');
      setUploadMsg('шаблон выгружен');
    } catch (e: any) {
      setUploadMsg('ошибка выгрузки шаблона: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsArticleMenuOpen(false);
    }
  }

  async function handleImportArticles(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      const res = await apiFetch('/api/articles/import', { method: 'POST', body: formData });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`загружено: ${json.inserted ?? 0}, обновлено: ${json.updated ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка загрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsArticleMenuOpen(false);
      if (articleFileRef.current) {
        articleFileRef.current.value = '';
      }
    }
  }

  async function handleReloadQueries() {
    if (!dateFrom || !dateTo) return;
    if (!confirm('Перевыгрузить запросы за выбранный период?')) return;
    setUploading(true);
    try {
      const res = await apiFetch('/api/queries/reload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFrom, dateTo }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`перевыгружено записей: ${json.saved}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка перевыгрузки: ' + (e.message || ''));
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteVisible() {
    if (requiresDateRange && (!dateFrom || !dateTo)) return;
    if (displayedRowIds.length === 0) return;
    const message = `Удалить все записи, отображаемые в таблице (${displayedRowIds.length})? ` +
      'Будут удалены записи с учетом текущих фильтров.';
    if (!confirm(message)) return;
    setUploading(true);
    try {
      const res = await apiFetch(`/api/bulk-delete/${schema.name}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: displayedRowIds }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(formatDeleteError(payload) || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`удалено записей: ${json.deleted ?? displayedRowIds.length}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка удаления: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsDeleteMenuOpen(false);
    }
  }

  async function handleDeleteByFilters() {
    if (requiresDateRange && (!dateFrom || !dateTo)) return;
    const payload = buildDeleteFiltersPayload();
    const message = 'Удалить записи по текущим фильтрам и диапазону дат? Действие необратимо.';
    if (!confirm(message)) return;
    setUploading(true);
    try {
      const res = await apiFetch(`/api/bulk-delete/${schema.name}/filters`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const responsePayload = await res.json().catch(() => null);
        throw new Error(formatDeleteError(responsePayload) || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`удалено записей: ${json.deleted ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка удаления: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsDeleteMenuOpen(false);
    }
  }

  async function handleDeleteByDateRange() {
    if (!dateFrom || !dateTo) return;
    const message = `Удалить записи по диапазону дат ${dateFrom} — ${dateTo}?`;
    if (!confirm(message)) return;
    setUploading(true);
    try {
      const res = await apiFetch(`/api/bulk-delete/${schema.name}/date-range`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFrom, dateTo }),
      });
      if (!res.ok) {
        const responsePayload = await res.json().catch(() => null);
        throw new Error(formatDeleteError(responsePayload) || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`удалено записей: ${json.deleted ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка удаления: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsDeleteMenuOpen(false);
    }
  }

  async function handleDeleteEverything() {
    const message = 'Удалить все записи из таблицы? Действие необратимо.';
    if (!confirm(message)) return;
    setUploading(true);
    try {
      const res = await apiFetch(`/api/bulk-delete/${schema.name}/all`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const responsePayload = await res.json().catch(() => null);
        throw new Error(formatDeleteError(responsePayload) || res.statusText);
      }
      const json = await res.json();
      setUploadMsg(`удалено записей: ${json.deleted ?? 0}`);
      loadData(true);
    } catch (e: any) {
      setUploadMsg('ошибка удаления: ' + (e.message || ''));
    } finally {
      setUploading(false);
      setIsDeleteMenuOpen(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setAdding(true)}
              disabled={uploading}
            >
              Добавить
            </button>
            {isArticles && (
              <div className="relative" data-article-menu-root ref={articleMenuRef}>
                <button
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  onClick={() => setIsArticleMenuOpen(open => !open)}
                  disabled={uploading}
                >
                  Импорт/Экспорт
                </button>
                {isArticleMenuOpen && (
                  <div className="absolute left-0 z-20 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={() => articleFileRef.current?.click()}
                    >
                      Загрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleExportArticles}
                    >
                      Выгрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleTemplateArticles}
                    >
                      Шаблон
                    </button>
                  </div>
                )}
                <input
                  ref={articleFileRef}
                  type="file"
                  accept="application/json"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImportArticles(file);
                    }
                  }}
                />
              </div>
            )}
            {isFinanceOps && (
              <button
                className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                onClick={() => setMode(mode === 'table' ? 'tree' : 'table')}
              >
                {mode === 'table' ? 'Расширенный режим' : 'Стандартный режим'}
              </button>
            )}
            {isMappings && (
              <div className="relative" data-mapping-menu-root ref={mappingMenuRef}>
                <button
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  onClick={() => setIsMappingMenuOpen(open => !open)}
                  disabled={uploading}
                >
                  Импорт/Экспорт
                </button>
                {isMappingMenuOpen && (
                  <div className="absolute left-0 z-20 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={() => mappingFileRef.current?.click()}
                    >
                      Загрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleExportMappings}
                    >
                      Выгрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleTemplateMappings}
                    >
                      Шаблон
                    </button>
                  </div>
                )}
                <input
                  ref={mappingFileRef}
                  type="file"
                  accept="application/json"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImportMappings(file);
                    }
                  }}
                />
              </div>
            )}
        {schema.name === 'fact_records' && (
          <button
            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => setShowUpload(true)}
            disabled={uploading}
          >
            Загрузить данные
          </button>
        )}
        {schema.name === 'fact_records' && (
          <button
            className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleCreateOps}
            disabled={uploading || !dateFrom || !dateTo}
          >
            Создать ФО
          </button>
        )}
        {requiresDateRange && (
          <>
            <input
              type="date"
              className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
            />
            <input
              type="date"
              className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
            />
            <button
              className="ml-2 inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              onClick={() => loadData(true)}
            >
              Применить
            </button>
            {isQueries && (
              <button
                className="ml-2 inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={handleReloadQueries}
                disabled={uploading || !dateFrom || !dateTo}
              >
                Перевыгрузить
              </button>
            )}
            {schema.name === 'finance_operations' && (
              <div className="relative ml-2" data-finance-menu-root ref={financeMenuRef}>
                <button
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  onClick={() => setIsFinanceMenuOpen(open => !open)}
                  disabled={uploading}
                >
                  Импорт/Экспорт
                </button>
                {isFinanceMenuOpen && (
                  <div className="absolute left-0 z-20 mt-2 w-48 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={() => financeFileRef.current?.click()}
                    >
                      Загрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleExportFinance}
                    >
                      Выгрузить
                    </button>
                    <button
                      className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
                      onClick={handleTemplateFinance}
                    >
                      Шаблон
                    </button>
                  </div>
                )}
                <input
                  ref={financeFileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImportFinance(file);
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
        {schema.name === 'plan_records' && (
          <>
            <input
              type="month"
              className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={month}
              onChange={e => setMonth(e.target.value)}
            />
            <button
              className="ml-2 inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              onClick={() => loadData(true)}
            >
              Применить
            </button>
            <button
              className="ml-2 inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setShowPlanFill(true)}
              disabled={uploading || !month}
            >
              Заполнить на основании
            </button>
          </>
        )}
        <button
          className="ml-2 inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setBulkEditing(true)}
          disabled={
            uploading ||
            displayedRowIds.length === 0 ||
            (requiresDateRange && (!dateFrom || !dateTo))
          }
        >
          Изменить
        </button>
        <div className="relative ml-2" data-delete-menu-root>
          <button
            className="inline-flex items-center rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => setIsDeleteMenuOpen(open => !open)}
            disabled={uploading}
          >
            Удалить
          </button>
          {isDeleteMenuOpen && (
            <div className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
              <button
                className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                onClick={handleDeleteVisible}
                disabled={
                  uploading ||
                  displayedRowIds.length === 0 ||
                  (requiresDateRange && (!dateFrom || !dateTo))
                }
              >
                <span className="font-medium">Удалить видимые</span>
                <span className="text-xs text-slate-400">({displayedRowIds.length})</span>
              </button>
              <button
                className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                onClick={handleDeleteByFilters}
                disabled={uploading || (requiresDateRange && (!dateFrom || !dateTo))}
              >
                <span className="font-medium">Удалить по фильтру</span>
                <span className="text-xs text-slate-400">фильтры + даты</span>
              </button>
              <button
                className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                onClick={handleDeleteByDateRange}
                disabled={uploading || !dateFrom || !dateTo}
              >
                <span className="font-medium">Удалить по диапазону</span>
                <span className="text-xs text-slate-400">только даты</span>
              </button>
              <button
                className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
                onClick={handleDeleteEverything}
                disabled={uploading}
              >
                <span className="font-medium">Удалить все</span>
                <span className="text-xs text-rose-400">из таблицы</span>
              </button>
            </div>
          )}
        </div>
        <button
          className="ml-2 inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          onClick={() => {
            setFilterInput({});
            setFilterSelect({});
          }}
        >
          Сбросить фильтры
        </button>
        {uploadMsg && <span className="ml-2 text-sm text-emerald-600">{uploadMsg}</span>}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              className="inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-700 shadow-sm transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleExportExcel}
              disabled={uploading || (requiresDateRange && (!dateFrom || !dateTo))}
              title="Выгрузить в Excel"
            >
              <svg
                className="h-4 w-4 text-emerald-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
                <path d="M14 2v6h6" />
                <path d="M8 15l2 2 4-4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div ref={containerRef} className="relative h-[70vh] overflow-auto overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></span>
              Загружаем данные...
            </div>
          </div>
        ) : loadError ? (
          <div className="py-6 text-center text-sm text-slate-500">{loadError}</div>
        ) : (
          <>
            <table className="min-w-full border-collapse border border-slate-200 text-sm text-slate-700">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr className="sticky top-0 z-10 bg-gray-50">
                  {visibleColumns.map(col => (
                    <th key={col.key} className="relative px-4 py-3 text-left" data-filter-root={col.key}>
                      <div className="flex items-center">
                        <span
                          className="flex-1 cursor-pointer text-xs font-semibold"
                          onClick={() => handleSort(col)}
                        >
                          {col.label}
                          {sort?.key === col.key && (
                            <span className="ml-1 text-xs">
                              {sort.direction === 'asc' ? '▲' : '▼'}
                            </span>
                          )}
                        </span>
                        <button
                          className="relative ml-1 rounded p-1 text-slate-400 transition hover:bg-slate-200 hover:text-slate-600"
                          onClick={e => {
                            e.stopPropagation();
                            setOpenFilter(openFilter === col.key ? null : col.key);
                          }}
                        >
                          🔽
                          {isColumnFiltered(col.key) && (
                            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-indigo-500"></span>
                          )}
                        </button>
                      </div>
                      {openFilter === col.key && (
                        <div className="absolute right-0 z-20 mt-2 max-h-60 w-48 overflow-auto rounded-xl border border-slate-200 bg-white p-3 shadow-lg">
                          <input
                            type="text"
                            placeholder="Фильтр"
                            className="mb-2 w-full rounded-lg border border-slate-200 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            value={filterInput[col.key] || ''}
                            onChange={e =>
                              setFilterInput(current => ({ ...current, [col.key]: e.target.value }))
                            }
                          />
                          <div className="max-h-40 space-y-1 overflow-auto text-xs">
                            {(() => {
                              const optionsForColumn = columnOptions[col.key] || [];
                              const searchValue = filterInput[col.key]?.trim().toLowerCase();
                              const selectedValues = filterSelect[col.key] || new Set();
                              const filteredOptions = optionsForColumn
                                .map(opt => ({ value: opt, label: getOptionLabel(col, opt) }))
                                .filter(opt => {
                                  if (!searchValue) return true;
                                  return opt.label.toLowerCase().includes(searchValue);
                                });
                              const sortedByLabel = (a: { label: string }, b: { label: string }) =>
                                a.label.localeCompare(b.label, 'ru');
                              const selected = filteredOptions
                                .filter(opt => selectedValues.has(opt.value))
                                .sort(sortedByLabel);
                              const unselected = filteredOptions
                                .filter(opt => !selectedValues.has(opt.value))
                                .sort(sortedByLabel);
                              const ordered = [...selected, ...unselected];
                              if (isFilterOptionsLoading && optionsForColumn.length === 0) {
                                return (
                                  <div className="flex items-center justify-center gap-2 py-2 text-xs text-slate-400">
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-indigo-600"></span>
                                    Загружаем значения...
                                  </div>
                                );
                              }
                              if (ordered.length === 0) {
                                return <div className="py-1 text-center text-xs text-slate-400">Нет данных</div>;
                              }
                              return ordered.map(opt => {
                                const checked = filterSelect[col.key]?.has(opt.value) || false;
                                return (
                                  <label key={String(opt.value)} className="flex items-center gap-2 text-slate-600">
                                    <input
                                      type="checkbox"
                                      className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                      checked={checked}
                                      onChange={e => {
                                        setFilterSelect(current => {
                                          const set = new Set(current[col.key] || []);
                                          if (e.target.checked) {
                                            set.add(opt.value);
                                          } else {
                                            set.delete(opt.value);
                                          }
                                          return { ...current, [col.key]: set };
                                        });
                                      }}
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                );
                              });
                            })()}
                          </div>
                          <button
                            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                            onClick={() => {
                              setFilterInput(cur => ({ ...cur, [col.key]: '' }));
                              setFilterSelect(cur => {
                                const n = { ...cur };
                                delete n[col.key];
                                return n;
                              });
                            }}
                          >
                            Сбросить
                          </button>
                        </div>
                      )}
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right"></th>
                </tr>
                {(schema.name === 'fact_records' || schema.name === 'finance_operations' || schema.name === 'plan_records') && (
                  <tr className="sticky top-9 z-0 bg-slate-100 text-xs font-semibold text-slate-600">
                    {visibleColumns.map(col => {
                      const keyCamel = toCamel(col.key);
                      const keySnake = toSnake(col.key);
                      const val =
                        col.key === firstVisibleKey
                          ? totals.count
                          : col.type === 'number'
                          ? totals[col.key] ?? totals[keyCamel] ?? totals[keySnake]
                          : undefined;
                      return (
                        <td key={col.key} className="px-4 py-2">
                          {val !== undefined ? formatNumber(val) : ''}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2"></td>
                  </tr>
                )}
              </thead>
              <tbody>
                {rowsToRender.map(({ row, level, hasChildren }) => (
                  <tr
                    key={getRowKey(row)}
                    data-row-id={getRowKey(row)}
                    tabIndex={-1}
                    className="cursor-pointer border-b border-slate-100 transition hover:bg-indigo-50/40"
                    onClick={() => setEditing(row)}
                  >
                    {visibleColumns.map((col, idx) => {
                      const refData =
                        col.type === 'reference' && references[col.key]
                          ? references[col.key].find(r => r.id === row[col.key])
                          : null;

                      const raw =
                        schema.name === 'users' && col.key === 'passwordHash'
                          ? '*****'
                          : refData
                          ? getRefLabel(refData, col.refTable)
                          : row[col.key];
                      let displayValue =
                        raw === 'None' || raw === null || raw === undefined ? '' : raw;
                      if (typeof displayValue === 'object') {
                        displayValue = stringifyObject(displayValue);
                      }
                      if (col.type === 'boolean' && displayValue !== '') {
                        displayValue = raw ? '✓' : '';
                      }
                      if (col.type === 'number' && displayValue !== '') {
                        displayValue = formatNumber(displayValue);
                      }
                      if (col.type === 'date' && displayValue) {
                        displayValue = formatDateValue(displayValue);
                      }
                      let content = displayValue;
                      if ((isArticles || (isFinanceOps && mode === 'tree')) && col.key === firstVisibleKey) {
                        content = (
                          <div style={{ paddingLeft: level * 16 }} className="flex items-center">
                            {hasChildren && (
                              <span
                                className="mr-1 cursor-pointer select-none"
                                onClick={event => {
                                  event.stopPropagation();
                                  setCollapsedRows(c => {
                                    const n = new Set(c);
                                    const rowKey = getRowKey(row);
                                    n.has(rowKey) ? n.delete(rowKey) : n.add(rowKey);
                                    return n;
                                  });
                                }}
                              >
                                {collapsedRows.has(getRowKey(row)) ? '▶' : '▼'}
                              </span>
                            )}
                            <span>{displayValue}</span>
                          </div>
                        );
                      }
                      return (
                        <td
                          key={col.key}
                          onDoubleClick={() => idx === 0 && !showId && setShowId(true)}
                          className="px-4 py-2 align-top"
                          style={
                            col.key === 'product_id'
                              ? {
                                  maxWidth: '80ch',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : undefined
                          }
                        >
                          {content}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <button
                        className="rounded-lg bg-rose-600 px-2 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={e => {
                          e.stopPropagation();
                          const recordId = getRowKey(row);
                          if (recordId === undefined || recordId === null) return;
                          handleDelete(recordId);
                        }}
                        disabled={uploading}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loadingMore && (
              <div className="py-3 text-center text-sm text-slate-500">Загрузка...</div>
            )}
          </>
        )}
      </div>
      {bulkEditing && (
        <BulkEditModal
          schema={schema}
          apiTableName={apiTableName}
          ids={displayedRowIds}
          filters={bulkFilters.filters}
          search={bulkFilters.search}
          dateFrom={requiresDateRange ? dateFrom : undefined}
          dateTo={requiresDateRange ? dateTo : undefined}
          month={schema.name === 'plan_records' ? month : undefined}
          onClose={() => setBulkEditing(false)}
          onSaved={updatedCount => {
            setUploadMsg(`обновлено записей: ${updatedCount}`);
            loadData(true);
          }}
        />
      )}
      <div
        className={`text-sm text-slate-600 ${
          totalCount && data.length < totalCount ? 'cursor-pointer underline decoration-dotted' : ''
        }`}
        onClick={handleLoadAll}
        title={totalCount ? 'Загрузить все записи' : undefined}
      >
        {isLoadingAll
          ? 'Загружаем все записи...'
          : `Выгружено записей: ${data.length}${totalCount !== null ? ` из ${totalCount}` : ''}`}
      </div>
      {adding && (
        <RecordModal
          schema={schema}
          onClose={() => setAdding(false)}
          onSaved={changed => {
            if (changed) {
              loadData(true);
            }
          }}
        />
      )}
      {editing && (
        <RecordModal
          schema={schema}
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={changed => {
            if (changed) {
              const rowKey = editing ? getRowKey(editing) : null;
              setFocusRowId(rowKey ?? null);
              loadData(true);
            }
          }}
        />
      )}
      {showUpload && (
        <UploadModal onClose={() => setShowUpload(false)} onUpload={handleUpload} uploading={uploading} />
      )}
      {showPlanFill && schema.name === 'plan_records' && (
        <PlanFillModal
          targetMonth={month}
          onClose={() => setShowPlanFill(false)}
          onSaved={() => {
            setShowPlanFill(false);
            loadData(true);
          }}
        />
      )}
    </div>
  );
};

export default DataTable;
